/**
 * tree_collider.ts — a walkable tree's collider IS its trunk (flora update,
 * 2026-09-07). The player must move THROUGH the woods: a bounding box or a
 * convex hull of a whole tree walls off the space under its canopy, so the
 * physics proxy is a static TRIMESH cut from the tree's own mesh — every
 * triangle that lies in the lower `cutFrac` of the tree and within
 * `radialFrac` of its footprint radius (the trunk and its buttress roots;
 * never a low branch reaching out under the crown). Model space, bottom
 * pivot; the physics module scales it per instance. Built from the cooked
 * LOD2 variant (a few thousand triangles) so a stand of near trees stays
 * cheap to insert.
 */
import type * as THREE from 'three/webgpu';
import type { ColliderProxy } from './collider_proxy.js';

export interface TrunkProxyOptions { cutFrac?: number; radialFrac?: number; maxTris?: number }

export function trunkProxyFromGeometry(geo: THREE.BufferGeometry, { cutFrac = 0.34, radialFrac = 0.42, maxTris = 3000 }: TrunkProxyOptions = {}): ColliderProxy | null {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!pos || pos.count < 3) return null;
  const n = pos.count;
  let minY = Infinity, maxY = -Infinity, minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (let i = 0; i < n; i++) { const y = pos.getY(i), x = pos.getX(i), z = pos.getZ(i); if (y < minY) minY = y; if (y > maxY) maxY = y; if (x < minX) minX = x; if (x > maxX) maxX = x; if (z < minZ) minZ = z; if (z > maxZ) maxZ = z; }
  const h = maxY - minY; if (!(h > 0)) return null;
  const cut = minY + h * cutFrac;
  const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
  const rMax = Math.max(maxX - minX, maxZ - minZ) / 2 * radialFrac;
  const index = geo.getIndex();
  const triN = index ? index.count / 3 : Math.floor(n / 3);
  const kept: number[] = [];
  for (let t = 0; t < triN; t++) {
    const a = index ? index.getX(t * 3) : t * 3, b = index ? index.getX(t * 3 + 1) : t * 3 + 1, c = index ? index.getX(t * 3 + 2) : t * 3 + 2;
    const ya = pos.getY(a), yb = pos.getY(b), yc = pos.getY(c);
    if ((ya + yb + yc) / 3 > cut) continue; // by centroid: a trunk ring straddling the cut stays
    const mx = (pos.getX(a) + pos.getX(b) + pos.getX(c)) / 3 - cx, mz = (pos.getZ(a) + pos.getZ(b) + pos.getZ(c)) / 3 - cz;
    if (Math.hypot(mx, mz) > rMax) continue;
    kept.push(a, b, c);
  }
  if (kept.length < 9) return null;
  // decimate by stride when the trunk still carries too many triangles
  let tris = kept;
  if (kept.length / 3 > maxTris) {
    const stride = Math.ceil(kept.length / 3 / maxTris); tris = [];
    for (let t = 0; t < kept.length / 3; t += stride) tris.push(kept[t * 3], kept[t * 3 + 1], kept[t * 3 + 2]);
  }
  // compact the vertex set
  const remap = new Map<number, number>(); const positions: number[] = []; const indices: number[] = [];
  for (const v of tris) {
    let r = remap.get(v);
    if (r === undefined) { r = positions.length / 3; remap.set(v, r); positions.push(pos.getX(v), pos.getY(v), pos.getZ(v)); }
    indices.push(r);
  }
  return { shape: 'trimesh_static', positions, indices };
}
